/**
 * HudContainer - Comprehensive Air Quality Dashboard
 * Interactive metrics with educational tooltips for first-time users
 */

import { useState, useEffect } from 'react';
import { useAirQualityStore } from '@/stores';
import { cn } from '@/utils/cn';

interface HudContainerProps {
  isMobile?: boolean;
  className?: string;
}

// Metric explanations for tooltips
const METRIC_INFO: Record<string, { title: string; description: string; scale?: string }> = {
  aqi: {
    title: 'Air Quality Index',
    description: 'A standardized indicator of overall air quality. Lower is better. Based on PM2.5, ozone, and other pollutants.',
    scale: '0-50: Good | 51-100: Moderate | 101-150: Unhealthy for sensitive | 151-200: Unhealthy | 201+: Hazardous'
  },
  pm25: {
    title: 'PM2.5 (Fine Particles)',
    description: 'Tiny particles smaller than 2.5 micrometers that can penetrate deep into lungs and bloodstream. Major health concern.',
    scale: '0-12: Good | 12-35: Moderate | 35+: Unhealthy'
  },
  ozone: {
    title: 'Ozone (O₃)',
    description: 'Ground-level ozone forms from vehicle emissions and sunlight. Can trigger asthma and reduce lung function.',
    scale: '0-54: Good | 55-70: Moderate | 70+: Unhealthy'
  },
  co2: {
    title: 'Carbon Dioxide (CO₂)',
    description: 'Indicator of ventilation and air freshness. High levels cause drowsiness and reduced cognitive function.',
    scale: '400-600: Fresh | 600-1000: Moderate | 1000+: Poor'
  },
  temp: {
    title: 'Temperature',
    description: 'Current outdoor temperature. Important for planning exercise intensity and hydration needs.',
  },
  humidity: {
    title: 'Humidity',
    description: 'Amount of moisture in the air. Affects how temperature feels and your body\'s ability to cool down through sweat.',
    scale: '30-50%: Ideal | 50-70%: Comfortable | 70%+: Humid'
  },
  uv: {
    title: 'UV Index',
    description: 'Strength of ultraviolet radiation from the sun. Higher values mean faster skin damage and higher sunburn risk.',
    scale: '0-2: Low | 3-5: Moderate | 6-7: High | 8+: Very High'
  },
  wind: {
    title: 'Wind Speed',
    description: 'How fast air is moving. Affects perceived temperature and can help disperse pollutants.',
  },
  forecast: {
    title: '2-Hour Forecast',
    description: 'Predicted air quality for the next 2 hours. Bars show AQI trend - green is good, yellow is moderate.',
  }
};

export function HudContainer({
  isMobile = false,
  className,
}: HudContainerProps) {
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [open, setOpen] = useState(!isMobile);
  const [hasLoaded, setHasLoaded] = useState(false);

  const aqi = useAirQualityStore((state) => state.currentReading?.aqi);
  const pm25 = useAirQualityStore((state) => state.pm25);
  const ozone = useAirQualityStore((state) => state.ozone);
  const co2 = useAirQualityStore((state) => state.co2);

  const currentAqi = aqi || 42;

  // Disable animation on initial load to prevent jarring effect
  useEffect(() => {
    const timer = setTimeout(() => setHasLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // AQI status
  const getAqiInfo = (value: number) => {
    if (value <= 50) return { label: 'Good', color: '#22c55e', message: 'Great for outdoor activities' };
    if (value <= 100) return { label: 'Moderate', color: '#facc15', message: 'Acceptable air quality' };
    if (value <= 150) return { label: 'Sensitive', color: '#fb923c', message: 'Sensitive groups take care' };
    if (value <= 200) return { label: 'Unhealthy', color: '#f87171', message: 'Limit outdoor exposure' };
    return { label: 'Hazardous', color: '#c084fc', message: 'Stay indoors' };
  };

  const info = getAqiInfo(currentAqi);

  // Ring calculations
  const size = isMobile ? 90 : 110;
  const strokeWidth = 7;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(currentAqi / 300, 1);
  const strokeDashoffset = circumference * (1 - progress);

  // Safe value checks
  const pm25Val = pm25?.value && pm25.value < 500 ? pm25.value : 12;
  const ozoneVal = ozone?.value && ozone.value < 500 ? ozone.value : 34;
  const co2Val = co2?.value && co2.value < 5000 ? co2.value : 410;

  // Pollutant status
  const getPollutantStatus = (type: 'pm25' | 'ozone' | 'co2', value: number) => {
    if (type === 'pm25') {
      if (value <= 12) return '#22c55e';
      if (value <= 35) return '#facc15';
      return '#f87171';
    }
    if (type === 'ozone') {
      if (value <= 54) return '#22c55e';
      if (value <= 70) return '#facc15';
      return '#f87171';
    }
    if (value <= 600) return '#22c55e';
    if (value <= 1000) return '#facc15';
    return '#f87171';
  };

  const handleMetricClick = (metricId: string) => {
    setActiveTooltip(activeTooltip === metricId ? null : metricId);
  };

  return (
    <div className={cn('relative w-full h-full pointer-events-none', className)}>
      {/* Tooltip Overlay */}
      {activeTooltip && (
        <div
          className="fixed inset-0 z-50 pointer-events-auto"
          onClick={() => setActiveTooltip(null)}
        >
          <div className={cn(
            'absolute max-w-xs',
            isMobile ? 'top-4 left-4 right-4' : 'top-4 left-4 mt-2 ml-64'
          )}>
            <div
              className="bg-slate-800 border border-slate-600 rounded-xl p-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-white">
                  {METRIC_INFO[activeTooltip]?.title}
                </h3>
                <button
                  onClick={() => setActiveTooltip(null)}
                  className="text-slate-400 hover:text-white text-lg leading-none"
                >
                  ×
                </button>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                {METRIC_INFO[activeTooltip]?.description}
              </p>
              {METRIC_INFO[activeTooltip]?.scale && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Scale</div>
                  <p className="text-[11px] text-slate-300">
                    {METRIC_INFO[activeTooltip]?.scale}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panel + side tab wrapper — slides as one unit */}
      <div
        className={cn(
          'absolute top-4 left-0 pointer-events-auto transition-transform duration-300 ease-in-out',
          isMobile && !open && '-translate-x-[calc(100%-2rem)]'
        )}
        style={isMobile ? { display: 'flex', alignItems: 'center', height: 'calc(100vh - 2rem)' } : { display: 'flex', alignItems: 'flex-start' }}
      >
        {/* Panel content */}
        <div className={cn('ml-4', isMobile && 'max-w-[220px]')}>
          <div
            className="rounded-2xl overflow-hidden bg-slate-900/90 backdrop-blur-xl border border-slate-700/40"
            style={{ boxShadow: '0 4px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)' }}
          >
          {/* AQI Section - Clickable */}
          <button
            onClick={() => handleMetricClick('aqi')}
            className={cn(
              'w-full p-3 text-left transition-colors',
              isMobile ? 'pb-2' : 'p-4 pb-3',
              activeTooltip === 'aqi' ? 'bg-slate-800/50' : 'hover:bg-slate-800/30'
            )}
          >
            <div className="flex items-start gap-3">
              {/* Circular Gauge */}
              <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="transform -rotate-90">
                  <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#1e293b"
                    strokeWidth={strokeWidth}
                  />
                  <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={info.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    className={hasLoaded ? "transition-all duration-1000 ease-out" : ""}
                    style={{ filter: `drop-shadow(0 0 8px ${info.color}80)` }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className={cn('font-bold tabular-nums', isMobile ? 'text-xl' : 'text-2xl')}
                    style={{ color: info.color }}
                  >
                    {currentAqi}
                  </span>
                  <span className="text-[8px] text-slate-500 uppercase tracking-widest">AQI</span>
                </div>
              </div>

              {/* Status & Message */}
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{ backgroundColor: `${info.color}20`, color: info.color }}
                  >
                    {info.label}
                  </span>
                  <InfoIcon className="w-3 h-3 text-slate-500" />
                </div>
                <p className="mt-1.5 text-[10px] text-slate-400 leading-relaxed">
                  {info.message}
                </p>
              </div>
            </div>
          </button>

          {/* Pollutants Section */}
          <div className="px-3 py-2.5 border-t border-slate-700/40 bg-slate-800/30">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
              Pollutants
            </div>
            <div className="grid grid-cols-3 gap-2">
              <PollutantCard
                label="PM2.5"
                value={pm25Val}
                unit="µg/m³"
                color={getPollutantStatus('pm25', pm25Val)}
                trend={pm25?.trend}
                isActive={activeTooltip === 'pm25'}
                onClick={() => handleMetricClick('pm25')}
              />
              <PollutantCard
                label="O₃"
                value={ozoneVal}
                unit="ppb"
                color={getPollutantStatus('ozone', ozoneVal)}
                trend={ozone?.trend}
                isActive={activeTooltip === 'ozone'}
                onClick={() => handleMetricClick('ozone')}
              />
              <PollutantCard
                label="CO₂"
                value={co2Val}
                unit="ppm"
                color={getPollutantStatus('co2', co2Val)}
                trend={co2?.trend}
                isActive={activeTooltip === 'co2'}
                onClick={() => handleMetricClick('co2')}
              />
            </div>
          </div>

          {/* Weather Conditions */}
          <div className="px-3 py-2.5 border-t border-slate-700/40">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
              Conditions
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              <WeatherChip
                icon={<TempIcon className="w-3.5 h-3.5" />}
                value="24°"
                label="temp"
                color="#22d3ee"
                isActive={activeTooltip === 'temp'}
                onClick={() => handleMetricClick('temp')}
              />
              <WeatherChip
                icon={<HumidityIcon className="w-3.5 h-3.5" />}
                value="65%"
                label="humid"
                color="#60a5fa"
                isActive={activeTooltip === 'humidity'}
                onClick={() => handleMetricClick('humidity')}
              />
              <WeatherChip
                icon={<UVIcon className="w-3.5 h-3.5" />}
                value="4"
                label="UV"
                color="#fbbf24"
                isActive={activeTooltip === 'uv'}
                onClick={() => handleMetricClick('uv')}
              />
              <WeatherChip
                icon={<WindIcon className="w-3.5 h-3.5" />}
                value="8"
                label="km/h"
                color="#a78bfa"
                isActive={activeTooltip === 'wind'}
                onClick={() => handleMetricClick('wind')}
              />
            </div>
          </div>

          {/* Mini Forecast - Clickable */}
          <button
            onClick={() => handleMetricClick('forecast')}
            className={cn(
              'w-full px-3 py-2.5 border-t border-slate-700/40 text-left transition-colors',
              activeTooltip === 'forecast' ? 'bg-slate-700/50' : 'bg-slate-800/20 hover:bg-slate-800/40'
            )}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] text-slate-500 uppercase tracking-wider font-medium">
                  2hr Forecast
                </span>
                <InfoIcon className="w-2.5 h-2.5 text-slate-500" />
              </div>
              <span className="text-[9px] text-emerald-400 font-medium">Stable</span>
            </div>
            <div className="flex items-end gap-0.5 h-6">
              {[42, 45, 43, 48, 46, 44, 42, 40].map((val, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm transition-all"
                  style={{
                    height: `${Math.max((val / 80) * 100, 15)}%`,
                    backgroundColor: val <= 50 ? '#22c55e50' : val <= 100 ? '#facc1550' : '#f8717150',
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-1 text-[8px] text-slate-500">
              <span>Now</span>
              <span>+2h</span>
            </div>
          </button>
          </div>

          {/* Help hint for first-time users */}
          {!isMobile && (
            <div className="mt-2 text-[9px] text-slate-500 text-center">
              Tap any metric for details
            </div>
          )}
        </div>

        {/* Side tab toggle button (mobile only) */}
        {isMobile && (
          <button
            onClick={() => { setOpen(!open); if (open) setActiveTooltip(null); }}
            className="flex-shrink-0 flex items-center justify-center w-8 rounded-r-xl bg-slate-900/90 backdrop-blur-xl border border-l-0 border-slate-700/40"
            style={{
              height: 64,
              boxShadow: '2px 0 15px rgba(0,0,0,0.3)',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke={info.color}
              strokeWidth="2"
              strokeLinecap="round"
              className={cn('transition-transform duration-300', open ? 'rotate-0' : 'rotate-180')}
            >
              <path d="M9 2L4 7l5 5" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// Pollutant Card - Clickable
function PollutantCard({
  label,
  value,
  unit,
  color,
  trend,
  isActive,
  onClick
}: {
  label: string;
  value: number;
  unit: string;
  color: string;
  trend?: 'rising' | 'falling' | 'stable';
  isActive: boolean;
  onClick: () => void;
}) {
  const trendIcon = trend === 'rising' ? '↑' : trend === 'falling' ? '↓' : '';
  const trendColor = trend === 'rising' ? '#f87171' : trend === 'falling' ? '#22c55e' : '';

  return (
    <button
      onClick={onClick}
      className={cn(
        'text-center p-1.5 rounded-lg transition-colors',
        isActive ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'
      )}
    >
      <div className="flex items-center justify-center gap-1 text-[9px] text-slate-500 mb-0.5">
        {label}
        <InfoIcon className="w-2.5 h-2.5" />
      </div>
      <div className="flex items-center justify-center gap-0.5">
        <span className="text-xs font-semibold tabular-nums" style={{ color }}>
          {Math.round(value)}
        </span>
        {trend && trend !== 'stable' && (
          <span className="text-[9px]" style={{ color: trendColor }}>{trendIcon}</span>
        )}
      </div>
      <div className="text-[8px] text-slate-500">{unit}</div>
    </button>
  );
}

// Weather Chip - Professional card-style button
function WeatherChip({
  icon,
  value,
  label,
  color,
  isActive,
  onClick
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  color: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center py-1.5 rounded-lg transition-all',
        isActive ? 'bg-slate-700/60 ring-1 ring-slate-600' : 'hover:bg-slate-700/30'
      )}
    >
      <span style={{ color }} className="opacity-80">{icon}</span>
      <span className="text-[11px] font-semibold text-white tabular-nums mt-0.5">{value}</span>
      <span className="text-[8px] text-slate-500 uppercase tracking-wider">{label}</span>
    </button>
  );
}

// SVG weather icons — clean, consistent, professional
function TempIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
    </svg>
  );
}

function HumidityIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  );
}

function UVIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function WindIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M17.7 7.7A2.5 2.5 0 0 1 17 13H2" />
      <path d="M9.6 4.6A2 2 0 0 1 11 8H2" />
      <path d="M12.6 19.4A2 2 0 0 0 14 16H2" />
    </svg>
  );
}

// Info icon
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className={className}>
      <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.93 12.14h-1.9V6.67h1.9v5.47zm-.95-6.59a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2z"/>
    </svg>
  );
}

export default HudContainer;
